/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  getPayoutsByUserCollections,
  checkOrdersPaidStatus,
  getPayoutsByOrderIds,
  PayoutRecord
} from '../services/payoutService.ts';

/**
 * Hook for getting all payouts across a user's collections
 * Use this for REGULAR USERS based on their tags array
 */
export const useUserPayouts = (userTags: string[]) => {
  const [payouts, setPayouts] = useState<PayoutRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [summaryByCollection, setSummaryByCollection] = useState<Map<string, { count: number; total: number; payouts: PayoutRecord[] }>>(new Map());

  const loadPayouts = async () => {
    console.log('🔍 useUserPayouts - loadPayouts called');
    console.log('🏷️ userTags (handles):', userTags);
    
    if (!userTags || userTags.length === 0) {
      console.log('⚠️ No user tags, skipping payout load');
      setPayouts([]);
      setTotalAmount(0);
      setSummaryByCollection(new Map());
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('📡 Fetching payouts for collection handles:', userTags);
      const fetchedPayouts = await getPayoutsByUserCollections(userTags);
      console.log('✅ Fetched payouts:', fetchedPayouts);
      console.log('📊 Number of payouts fetched:', fetchedPayouts.length);
      
      // Log each payout's collectionHandle and collectionTitle for debugging
      fetchedPayouts.forEach((payout, index) => {
        console.log(`Payout ${index}:`, {
          id: payout.id,
          collectionTitle: payout.collectionTitle,
          collectionHandle: payout.collectionHandle,
          amount: payout.amount
        });
      });
      
      // ADDED: Deduplicate payouts by ID (in case a product belongs to multiple collections)
      const uniquePayoutsMap = new Map<string, PayoutRecord>();
      fetchedPayouts.forEach(payout => {
        if (!uniquePayoutsMap.has(payout.id)) {
          uniquePayoutsMap.set(payout.id, payout);
        } else {
          console.log('⚠️ Duplicate payout found:', payout.id);
        }
      });
      
      const uniquePayouts = Array.from(uniquePayoutsMap.values());
      console.log('✅ Unique payouts after deduplication:', uniquePayouts.length);
      
      setPayouts(uniquePayouts);
      
      // Calculate total from unique payouts
      const total = uniquePayouts.reduce((sum, payout) => sum + (payout.amount || 0), 0);
      setTotalAmount(total);
      console.log('💰 Total amount:', total);
      
      // Update user's total paid in Firestore (only if changed)
      const currentUser = auth.currentUser;
      if (currentUser) {
        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          
          // Get current totalPaid from Firestore
          const userDoc = await getDoc(userDocRef);
          const currentTotalPaid = userDoc.data()?.totalPaid || 0;
          
          // Only update if the total has changed
          if (currentTotalPaid !== total) {
            await updateDoc(userDocRef, {
              totalPaid: total
            });
            console.log(`✅ Updated user total paid in Firestore: ${currentTotalPaid} → ${total}`);
          } else {
            console.log('ℹ️ Total paid unchanged, skipping Firestore update');
          }
        } catch (updateError) {
          console.error('❌ Error updating user total paid:', updateError);
          // Don't throw - this is a non-critical update
        }
      }
      
      // Build summary by collection (using unique payouts)
      const summary = new Map<string, { count: number; total: number; payouts: PayoutRecord[] }>();
      
      uniquePayouts.forEach(payout => {
        const existing = summary.get(payout.collectionTitle) || { count: 0, total: 0, payouts: [] };
        summary.set(payout.collectionTitle, {
          count: existing.count + 1,
          total: existing.total + payout.amount,
          payouts: [...existing.payouts, payout]
        });
      });
      
      setSummaryByCollection(summary);
      console.log('📊 Summary by collection:', Array.from(summary.entries()));
      
      console.log(`✅ Loaded ${uniquePayouts.length} unique payouts (from ${fetchedPayouts.length} total), total: $${total}`);
    } catch (err) {
      console.error('❌ Error loading payouts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load payouts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🔄 useUserPayouts - useEffect triggered, userTags:', userTags);
    loadPayouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(userTags)]);

  return {
    payouts,
    loading,
    error,
    totalAmount,
    summaryByCollection,
    refreshPayouts: loadPayouts
  };
};

/**
 * Hook for checking order paid status by order numbers
 * Works for both admins and regular users
 */
export const useOrderPaidStatus = (
  orderIds: string[],
  orderNumbers: string[],
  collectionHandles: string[] = []
) => {
  const [paidStatus, setPaidStatus] = useState<Map<string, boolean>>(new Map());
  const [payoutDetailsMap, setPayoutDetailsMap] = useState<Map<string, PayoutRecord>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPaidStatus = async () => {
    console.log('🔍 loadPaidStatus called with:', {
      orderIdsCount: orderIds.length,
      orderNumbersCount: orderNumbers.length,
      sampleOrderIds: orderIds.slice(0, 3),
      sampleOrderNumbers: orderNumbers.slice(0, 3)
    });
    
    if (orderIds.length === 0 || orderNumbers.length === 0) {
      setPaidStatus(new Map());
      setPayoutDetailsMap(new Map());
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('📡 Calling checkOrdersPaidStatus...');
      const status = await checkOrdersPaidStatus(orderIds, orderNumbers, collectionHandles);
      console.log('✅ Paid status result:', status);
      console.log('📊 Number of paid orders:', Array.from(status.values()).filter(Boolean).length);
      
      setPaidStatus(status);
      
      // Fetch full payout details for paid orders
      const paidOrderIds = Array.from(status.entries())
        .filter(([_, isPaid]) => isPaid)
        .map(([orderId, _]) => orderId);
      
      console.log('💰 Paid order IDs:', paidOrderIds);
      
      if (paidOrderIds.length > 0) {
        console.log('📡 Fetching payout details for paid orders...');
        const payouts = await getPayoutsByOrderIds(paidOrderIds, collectionHandles);
        console.log('✅ Fetched payouts:', payouts);
        
        // Map payouts to order IDs
        const detailsMap = new Map<string, PayoutRecord>();
        payouts.forEach(payout => {
          console.log('🔍 Processing payout:', payout.id, 'Orders:', payout.orders);
          
          // UPDATED: Support new 'orders' structure
          if (payout.orders && Array.isArray(payout.orders)) {
            payout.orders.forEach((order: any) => {
              console.log('  ↳ Mapping order:', order.id, 'to payout');
              detailsMap.set(order.id, payout);
            });
          }
          // Fallback for old 'orderIds' structure (for backward compatibility)
          else if (payout.orderIds && Array.isArray(payout.orderIds)) {
            payout.orderIds.forEach((orderId: string) => {
              console.log('  ↳ Mapping old format order:', orderId, 'to payout');
              detailsMap.set(orderId, payout);
            });
          }
        });
        
        console.log('✅ Payout details map created:', detailsMap);
        setPayoutDetailsMap(detailsMap);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load paid status');
      console.error('❌ Error loading paid status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🔄 useOrderPaidStatus useEffect triggered');
    loadPaidStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(orderIds), JSON.stringify(orderNumbers), JSON.stringify(collectionHandles)]);

  const getPayoutDetails = (orderId: string): PayoutRecord | null => {
    const result = payoutDetailsMap.get(orderId);
    return result || null;
  };

  return {
    paidStatus,
    loading,
    error,
    refreshPaidStatus: loadPaidStatus,
    isOrderPaid: (orderId: string) => {
      const isPaid = paidStatus.get(orderId) || false;
      console.log(`🔍 isOrderPaid(${orderId}):`, isPaid);
      return isPaid;
    },
    getPayoutDetails
  };
};